import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Size and place of the detached video card.
 *
 * The card is the video store's own element, created outside React, so the
 * box is kept as inline styles on that element. Until the reader moves or
 * resizes it, the element keeps the corner the stylesheet gives it.
 */
export type KbFloatingBox = {
  x: number;
  y: number;
  width: number;
};

const MIN_WIDTH = 240;
const MAX_WIDTH = 720;
const EDGE = 8;
/** The player keeps a 16:9 frame, so the height follows the width. */
const RATIO = 9 / 16;
/** A pointer that moves less than this is a click, not a drag. */
const DRAG_SLOP = 4;

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), Math.max(min, max));

/** A pointer id the browser no longer knows throws; the drag goes on. */
const capture = (element: HTMLElement, pointerId: number) => {
  try {
    element.setPointerCapture(pointerId);
  } catch {
    // Pointer already released.
  }
};

type DragState = {
  pointerId: number;
  startX: number;
  startY: number;
  originX: number;
  originY: number;
  captured: boolean;
};

export type KbFloatingCorner = 'nw' | 'ne' | 'sw' | 'se';

type ResizeState = {
  pointerId: number;
  corner: KbFloatingCorner;
  startX: number;
  startRect: { x: number; y: number; width: number };
};

const SIDES = ['left', 'top', 'width', 'right', 'bottom'] as const;

const clearBox = (element: HTMLElement | null) => {
  if (!element) return;

  for (const property of SIDES) element.style.removeProperty(property);
};

/**
 * Drag by the picture, resize by any corner.
 *
 * The pointer is captured only once the reader has actually moved it, so a
 * plain click still reaches the player and toggles playback. A real drag
 * takes the capture, and the player never sees it end as a tap.
 *
 * The box is written to the corner element — the player's host is a child of
 * it — so the article slot keeps the host at full width when the video goes
 * back.
 */
export const useKbFloatingBox = (host: HTMLElement, active: boolean) => {
  const [box, setBox] = useState<KbFloatingBox | null>(null);
  const drag = useRef<DragState | null>(null);
  const resize = useRef<ResizeState | null>(null);
  const styled = useRef<HTMLElement | null>(null);

  /** The card on screen: the corner while detached, the slot otherwise. */
  const card = useCallback(
    (): HTMLElement => (active ? host.parentElement ?? host : host),
    [host, active],
  );

  // Inline styles live on an element React does not own, so they are applied
  // and cleared here.
  useEffect(() => {
    const element = active ? host.parentElement ?? null : null;

    if (styled.current && styled.current !== element) clearBox(styled.current);

    styled.current = element;

    if (!element || !box) {
      clearBox(element);

      return;
    }

    element.style.left = `${box.x}px`;
    element.style.top = `${box.y}px`;
    element.style.width = `${box.width}px`;
    element.style.right = 'auto';
    element.style.bottom = 'auto';
  }, [host, active, box]);

  const onDragStart = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (event.button !== 0) return;

      const target = event.target as HTMLElement;

      // Buttons and the player's own controls keep their clicks.
      if (target.closest('button, [data-video-controls], [data-corner]')) {
        return;
      }

      const rect = card().getBoundingClientRect();

      drag.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: rect.left,
        originY: rect.top,
        captured: false,
      };
    },
    [card],
  );

  const onDragMove = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      const state = drag.current;

      if (!state || state.pointerId !== event.pointerId) return;

      const dx = event.clientX - state.startX;
      const dy = event.clientY - state.startY;

      if (!state.captured) {
        if (Math.abs(dx) < DRAG_SLOP && Math.abs(dy) < DRAG_SLOP) return;

        state.captured = true;
        capture(event.currentTarget, event.pointerId);
      }

      const rect = card().getBoundingClientRect();
      const width = rect.width;
      const height = width * RATIO;

      setBox({
        x: clamp(state.originX + dx, EDGE, window.innerWidth - width - EDGE),
        y: clamp(state.originY + dy, EDGE, window.innerHeight - height - EDGE),
        width,
      });
    },
    [card],
  );

  const onDragEnd = useCallback((event: React.PointerEvent<HTMLElement>) => {
    if (drag.current?.pointerId !== event.pointerId) return;

    drag.current = null;

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  const onResizeStart = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (event.button !== 0) return;

      const corner = event.currentTarget.dataset.corner as
        | KbFloatingCorner
        | undefined;

      if (!corner) return;

      event.stopPropagation();

      const rect = card().getBoundingClientRect();

      resize.current = {
        pointerId: event.pointerId,
        corner,
        startX: event.clientX,
        startRect: { x: rect.left, y: rect.top, width: rect.width },
      };
      capture(event.currentTarget, event.pointerId);
    },
    [card],
  );

  // The corner opposite the one being dragged stays where it is, so the card
  // grows from that corner. The frame keeps its ratio, so the height follows
  // the width.
  const onResizeMove = useCallback((event: React.PointerEvent<HTMLElement>) => {
    const state = resize.current;

    if (!state || state.pointerId !== event.pointerId) return;

    const start = state.startRect;
    const dx = event.clientX - state.startX;
    const width = clamp(
      state.corner === 'ne' || state.corner === 'se'
        ? start.width + dx
        : start.width - dx,
      MIN_WIDTH,
      Math.min(MAX_WIDTH, window.innerWidth - 2 * EDGE),
    );
    const height = width * RATIO;
    const right = start.x + start.width;
    const bottom = start.y + start.width * RATIO;

    setBox({
      x: clamp(
        state.corner === 'ne' || state.corner === 'se'
          ? start.x
          : right - width,
        EDGE,
        window.innerWidth - width - EDGE,
      ),
      y: clamp(
        state.corner === 'sw' || state.corner === 'se'
          ? start.y
          : bottom - height,
        EDGE,
        window.innerHeight - height - EDGE,
      ),
      width,
    });
  }, []);

  const onResizeEnd = useCallback((event: React.PointerEvent<HTMLElement>) => {
    if (resize.current?.pointerId !== event.pointerId) return;

    resize.current = null;

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  return {
    dragHandlers: {
      onPointerDown: onDragStart,
      onPointerMove: onDragMove,
      onPointerUp: onDragEnd,
      onPointerCancel: onDragEnd,
    },
    resizeHandlers: {
      onPointerDown: onResizeStart,
      onPointerMove: onResizeMove,
      onPointerUp: onResizeEnd,
      onPointerCancel: onResizeEnd,
    },
  };
};
